<template>
  <b-card class="shadow-sm text-center">
    <b-card-body class="p-6">
      <b-card-title class="mb-6">Meta de Faturamento</b-card-title>

      <!-- LOADING (mesmo padrão de skeleton em lista usado nos outros) -->
      <template v-if="loading">
        <ul class="list-unstyled m-0 p-0">
          <li v-for="i in 3" :key="i" class="d-flex align-items-center mb-6">
            <div
              class="d-flex justify-content-center align-items-center rounded-circle mr-4"
              style="width: 36px; height: 36px; background-color: #f3f6f9"
            >
              <b-skeleton width="18px" height="18px" />
            </div>
            <div class="flex-grow-1" style="width: 100%">
              <div class="d-flex justify-content-between">
                <b-skeleton width="40%" height="16px" />
                <b-skeleton width="20%" height="14px" />
              </div>
              <b-skeleton height="8px" class="mt-2" />
            </div>
          </li>
        </ul>
      </template>

      <!-- CONTEÚDO -->
      <template v-else>
        <div class="d-flex align-items-center justify-content-center">
          <div class="position-relative mr-6">
            <svg :width="size" :height="size">
              <circle
                class="circle-bg"
                :r="raio"
                :cx="center"
                :cy="center"
                fill="none"
              />
              <circle
                class="circle-progress"
                :r="raio"
                :cx="center"
                :cy="center"
                fill="none"
                :stroke-dasharray="circunferencia"
                :stroke-dashoffset="dashOffset"
              />
            </svg>
            <div
              class="position-absolute"
              :style="{
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }"
            >
              <span class="h4 font-weight-bold">{{ displayPercent }}</span>
            </div>
          </div>

          <div>
            <div class="mb-2">
              <small class="text-muted d-block">Meta</small>
              <span class="h6">{{ toCurrency(metaValue) }}</span>
            </div>
            <div>
              <small class="text-muted d-block">Atingido</small>
              <span
                :class="{
                  'text-success': percentNumber >= 100,
                  'text-warning': percentNumber < 100,
                }"
              >
                {{ toCurrency(atingidoValue) }}
              </span>
            </div>

            <div class="mt-3">
              <span
                class="badge"
                :class="
                  percentNumber >= 100 ? 'badge-success' : 'badge-primary'
                "
              >
                {{ percentNumber >= 100 ? "Meta Alcançada" : "Em Progresso" }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
export default {
  name: "MetaFaturamento",

  props: {
    // Você pode passar um objeto completo (ex.: { data: { meta, atingido, percentual } } ou direto { meta, atingido, percentual })
    dados: { type: Object, default: null },

    // Ou passar campos simples diretamente
    meta: { type: [Number, String], default: null },
    atingido: { type: [Number, String], default: null },
    percentual: { type: [Number, String], default: null },

    // Controle de loading vindo do pai (padrão igual aos outros)
    loading: { type: Boolean, default: false },

    // Aparência do gráfico circular
    size: { type: Number, default: 110 },
    stroke: { type: Number, default: 10 },
  },

  computed: {
    // Normaliza as fontes (dados > data > props simples)
    metaValue() {
      if (this.dados?.data?.meta != null) return Number(this.dados.data.meta);
      if (this.dados?.meta != null) return Number(this.dados.meta);
      if (this.meta != null) return Number(this.meta);
      return 0;
    },
    atingidoValue() {
      if (this.dados?.data?.atingido != null)
        return Number(this.dados.data.atingido);
      if (this.dados?.atingido != null) return Number(this.dados.atingido);
      if (this.atingido != null) return Number(this.atingido);
      return 0;
    },
    percentNumber() {
      if (this.dados?.data?.percentual != null)
        return Number(this.dados.data.percentual);
      if (this.dados?.percentual != null) return Number(this.dados.percentual);
      if (this.percentual != null) return Number(this.percentual);
      // fallback: calcula localmente se tiver meta/atingido
      if (this.metaValue > 0)
        return (this.atingidoValue / this.metaValue) * 100;
      return 0;
    },
    displayPercent() {
      const n = isFinite(this.percentNumber) ? this.percentNumber : 0;
      // duas casas, vírgula e símbolo %
      return `${n.toFixed(2).replace(".", ",")}%`;
    },

    // Desenho do círculo
    raio() {
      return (this.size - this.stroke) / 2;
    },
    center() {
      return this.size / 2;
    },
    circunferencia() {
      return 2 * Math.PI * this.raio;
    },
    dashOffset() {
      const pct = Math.max(0, Math.min(100, this.percentNumber));
      return this.circunferencia - (pct / 100) * this.circunferencia;
    },
  },

  methods: {
    toCurrency(value) {
      try {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(Number(value) || 0);
      } catch {
        const num = Number(value || 0)
          .toFixed(2)
          .replace(".", ",");
        return `R$ ${num}`;
      }
    },
  },
};
</script>

<style scoped>
.circle-bg {
  stroke: #e9ecef;
  stroke-width: 10;
}
.circle-progress {
  stroke: #28a745;
  stroke-width: 10;
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: center;
  transition: stroke-dashoffset 0.5s ease, stroke 0.3s ease;
}
</style>
